import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Building2, Users, Euro, Sparkles, AlertTriangle, Database,
  Activity, TrendingUp, Inbox, ShieldAlert,
} from "lucide-react";
import { fmtDateTime } from "@/lib/format";

export default function SuperAdminBusinessOverview() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/business-overview").then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (user && !user.is_super_admin) return <Navigate to="/" replace />;

  function fmtBytes(b) {
    if (!b) return "0 B";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
    return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
  }

  return (
    <div className="p-6 md:p-8" data-testid="business-overview-page">
      <div className="flex items-end justify-between mb-5">
        <div>
          <h2 className="font-display text-2xl font-semibold text-zinc-100 inline-flex items-center gap-2">
            <Activity className="w-6 h-6 text-emerald-400" />
            Owner Control Cockpit
            <span className="ml-2 text-[9px] uppercase tracking-wider px-2 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30">Super-Admin</span>
          </h2>
          <p className="text-xs text-zinc-500 mt-1">
            Live-KPIs der Plattform — Mandanten, Umsätze, Sicherheit, Storage & Aktivität.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm p-10 text-center">Lade KPIs…</div>
      ) : !data ? (
        <div className="text-amber-400 text-sm">Daten nicht verfügbar.</div>
      ) : (
        <>
          {/* Top KPI row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <KPI icon={Building2} label="Mandanten gesamt" value={data.tenants.total} sub={`${data.tenants.active} aktiv · ${data.tenants.trial} Trial`} testid="kpi-tenants" color="blue" />
            <KPI icon={Euro} label="MRR Schätzung" value={`${data.mrr_estimate_eur.toLocaleString("de-AT")} €`} sub="aktive Pakete + Add-ons" testid="kpi-mrr" color="emerald" />
            <KPI icon={Users} label="Aktive Benutzer" value={data.users.active} testid="kpi-users" color="purple" />
            <KPI icon={Inbox} label="Offene Anfragen" value={data.pending_extension_requests} sub="Add-on Aktivierungen" testid="kpi-requests" color="amber" />
          </div>

          {/* Lifecycle distribution */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4 mb-4">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3 inline-flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5" /> Lebenszyklus-Verteilung
            </div>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
              <Pill label="Trial" value={data.tenants.trial} cls="bg-blue-500/10 border-blue-500/30 text-blue-200" />
              <Pill label="Aktiv" value={data.tenants.active} cls="bg-emerald-500/10 border-emerald-500/30 text-emerald-200" />
              <Pill label="Suspendiert" value={data.tenants.suspended} cls="bg-amber-500/10 border-amber-500/30 text-amber-200" />
              <Pill label="Abgelaufen" value={data.tenants.expired} cls="bg-orange-500/10 border-orange-500/30 text-orange-200" />
              <Pill label="Gekündigt" value={data.tenants.cancelled} cls="bg-red-500/10 border-red-500/30 text-red-200" />
              <Pill label="Archiviert" value={data.tenants.archived} cls="bg-zinc-800 border-zinc-700 text-zinc-400" />
            </div>
          </div>

          {/* Two-column section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Add-on subscriptions */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3 inline-flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" /> Add-on Abos (zahlend)
              </div>
              {Object.keys(data.addon_subscriptions).length === 0 ? (
                <div className="text-zinc-500 text-sm">Noch keine zahlenden Abos.</div>
              ) : (
                <div className="space-y-1.5">
                  {Object.entries(data.addon_subscriptions).map(([name, count]) => (
                    <div key={name} className="flex items-center justify-between text-sm">
                      <span className="text-zinc-200">{name}</span>
                      <span className="font-mono text-emerald-300">{count} ×</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Module popularity */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3 inline-flex items-center gap-1.5">
                <Activity className="w-3.5 h-3.5" /> Modul-Popularität
              </div>
              <div className="space-y-1.5 max-h-56 overflow-y-auto">
                {Object.entries(data.module_popularity).slice(0, 12).map(([mod, count]) => (
                  <div key={mod} className="flex items-center justify-between text-sm">
                    <span className="text-zinc-300 font-mono uppercase tracking-wider text-[10px]">{mod}</span>
                    <span className="font-mono text-zinc-400">{count} Mandanten</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Security & storage */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <SecurityCard label="Failed Logins (24h)"
              value={data.security.failed_logins_24h}
              warning={data.security.failed_logins_24h > 10} />
            <SecurityCard label="Module-Blocks (24h)"
              value={data.security.module_blocks_24h}
              warning={data.security.module_blocks_24h > 50}
              hint="Hohe Werte können auf Berechtigungs-Konflikte hindeuten." />
            <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4" data-testid="kpi-backup">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 inline-flex items-center gap-1.5">
                <Database className="w-3.5 h-3.5" /> Letztes Backup
              </div>
              {data.last_backup ? (
                <>
                  <div className="text-sm text-zinc-100 font-mono">
                    {fmtDateTime(data.last_backup.at)}
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    {fmtBytes(data.last_backup.size)} · Status: <span className="text-emerald-400">{data.last_backup.status}</span>
                  </div>
                </>
              ) : (
                <div className="text-amber-400 text-sm">Noch kein Backup ausgeführt.</div>
              )}
            </div>
          </div>

          <div className="mt-4 text-[10px] text-zinc-500 font-mono text-right">
            Daten von {new Date(data.as_of).toLocaleString("de-AT")}
          </div>
        </>
      )}
    </div>
  );
}

function KPI({ icon: Icon, label, value, sub, testid, color = "blue" }) {
  const colorMap = {
    blue: "border-blue-500/30 bg-blue-500/5",
    emerald: "border-emerald-500/30 bg-emerald-500/5",
    purple: "border-purple-500/30 bg-purple-500/5",
    amber: "border-amber-500/30 bg-amber-500/5",
  };
  return (
    <div className={`rounded-md border p-4 ${colorMap[color] || colorMap.blue}`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">{label}</div>
          <div className="font-display text-2xl font-bold text-zinc-100">{value}</div>
          {sub && <div className="text-[11px] text-zinc-500 mt-1">{sub}</div>}
        </div>
        <Icon className="w-5 h-5 text-zinc-500" />
      </div>
    </div>
  );
}

function Pill({ label, value, cls }) {
  return (
    <div className={`rounded border px-3 py-2 ${cls}`}>
      <div className="text-[9px] uppercase tracking-wider opacity-70 mb-0.5">{label}</div>
      <div className="font-display font-bold text-base">{value}</div>
    </div>
  );
}

function SecurityCard({ label, value, warning, hint }) {
  return (
    <div className={`rounded-md border p-4 ${warning ? "border-amber-500/40 bg-amber-500/5" : "border-zinc-800 bg-zinc-900"}`}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 inline-flex items-center gap-1.5">
        {warning ? <AlertTriangle className="w-3.5 h-3.5 text-amber-400" /> : <ShieldAlert className="w-3.5 h-3.5" />}
        {label}
      </div>
      <div className={`font-display text-2xl font-bold ${warning ? "text-amber-300" : "text-zinc-100"}`}>{value}</div>
      {hint && <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}
